import { useEffect, useRef, useCallback } from 'react';
import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import { getHourAndMinFromTimeTrackerResult } from '@legacy/utils/time/timeTrackerTime';
import { eventKeyEscape, eventKeyTab } from '@legacy/constants/keyboardKey';
import ITimeTracker from '@legacy/apis/types/timeTrackerAPI';
import { TimeSpan } from './types';

export const useKeyboardControl = (
  isActive: boolean,
  setIsActive: (value: React.SetStateAction<boolean>) => void,
  currentTimeTrackerRef: HTMLDivElement | null,
) =>
  useEffect(() => {
    if (currentTimeTrackerRef) {
      if (isActive) {
        const focusableElements = getKeyboardFocusableElements(
          currentTimeTrackerRef,
        );

        currentTimeTrackerRef.addEventListener('keydown', (event) => {
          const isTabPress = event.key === eventKeyTab;
          if (focusableElements) {
            if (
              isTabPress &&
              event.shiftKey &&
              event.target === focusableElements[0]
            )
              setIsActive(false);
            else if (
              isTabPress &&
              event.target === focusableElements[focusableElements.length - 1]
            )
              setIsActive(false);
          }

          if (event.key === eventKeyEscape) {
            setIsActive(false);
            currentTimeTrackerRef.focus();
          }
        });
      }
      currentTimeTrackerRef.removeEventListener('keydown', (event) => {
        if (event.key === eventKeyEscape) {
          setIsActive(false);
        }
      });
      currentTimeTrackerRef.addEventListener('focusout', (event) => {
        if (
          !currentTimeTrackerRef?.contains(event.relatedTarget as HTMLElement)
        ) {
          setIsActive(false);
        }
      });
    }
  }, [isActive, setIsActive, currentTimeTrackerRef]);

export const useTimeCountDown = (
  timeTrackerData: ITimeTracker | undefined,
  serverTimeZoneId: string | undefined,
  setTimeSpan: React.Dispatch<React.SetStateAction<TimeSpan | undefined>>,
  isServerTimeZoneIdSuccess: boolean,
) => {
  const interval = useRef<NodeJS.Timeout | undefined>(undefined);
  const stopCountdown = useCallback(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }
  }, [interval]);

  const startCountdown = useCallback(() => {
    interval.current = setInterval(() => {
      setTimeSpan(
        getHourAndMinFromTimeTrackerResult(timeTrackerData, serverTimeZoneId),
      );
    }, 1000 * 15); // refresh every 15 sec
  }, [interval, timeTrackerData, serverTimeZoneId, setTimeSpan]);

  useEffect(() => {
    if (timeTrackerData && isServerTimeZoneIdSuccess) {
      startCountdown();
    } else {
      setTimeSpan(undefined);
      stopCountdown();
    }
  }, [
    timeTrackerData,
    setTimeSpan,
    startCountdown,
    stopCountdown,
    isServerTimeZoneIdSuccess,
  ]);
};
